<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import ToolboxIcon from './Common/ToolboxIcon.vue';
import { useGlobalState } from '../../composables/useGlobalState';

const route = useRoute();
const { userData, fetchUser, impersonatedUser, stopImpersonation } = useGlobalState();
const isSidebarOpen = ref(true);

const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value;
};

const allNavigation = [
    { name: 'Dashboard', href: '/toolbox', icon: 'DashboardIcon' },
    { name: 'Copy/Paste', href: '/toolbox/copy-paste', icon: 'ClipboardIcon', role: 'ROLE_TOOL_COPYPASTE' },
    { name: 'Todo List', href: '/toolbox/todo', icon: 'CheckIcon', role: 'ROLE_TOOL_TODO' },
    { name: 'Assistant IA', href: '/toolbox/ai-assistant', icon: 'Bot', role: 'ROLE_TOOL_AI_ASSISTANT' },
    { name: 'Simkl Sync', href: '/toolbox/simkl-sync', icon: 'CloudSync', role: 'ROLE_TOOL_SIMKLSYNC' },
    { name: 'Administration', href: '/toolbox/admin', icon: 'Settings', role: 'ROLE_ADMIN' },
];

const navigation = computed(() => {
    const roles = userData.value?.roles || [];
    const isAdmin = roles.includes('ROLE_ADMIN');
    const isToolAdmin = roles.includes('ROLE_TOOL_ADMIN');

    return allNavigation.filter(item => {
        if (!item.role) return true;
        if (isAdmin) return true;
        if (roles.includes(item.role)) return true;
        if (isToolAdmin && item.role.startsWith('ROLE_TOOL_')) return true;
        return false;
    });
});

onMounted(() => {
    fetchUser();
});
</script>

<template>
    <div class="toolbox-layout flex h-screen bg-background text-foreground">
        <!-- Sidebar -->
        <aside 
            :class="[
                'sidebar border-r border-border transition-all duration-300 ease-in-out',
                isSidebarOpen ? 'w-64' : 'w-20'
            ]"
        >
            <div class="flex h-full flex-col">
                <!-- Header Sidebar -->
                <div class="flex items-center justify-between p-4 border-b border-border">
                    <div v-if="isSidebarOpen" class="flex items-center space-x-2">
                        <div class="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
                            <ToolboxIcon name="Toolbox" :size="18" />
                        </div>
                        <span class="font-bold text-xl tracking-tight">Toolbox</span>
                    </div>
                    <button @click="toggleSidebar" class="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground">
                        <ToolboxIcon :name="isSidebarOpen ? 'ChevronLeft' : 'Bars'" :size="18" />
                    </button>
                </div>

                <!-- Nav -->
                <nav class="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
                    <router-link
                        v-for="item in navigation"
                        :key="item.name"
                        :to="item.href"
                        class="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors group"
                        :class="[
                            route.path === item.href 
                                ? 'bg-primary text-primary-foreground' 
                                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                        ]"
                    >
                        <ToolboxIcon :name="item.icon" :size="20" :class="[isSidebarOpen ? 'mr-3' : 'mx-auto']" />
                        <span v-if="isSidebarOpen" class="truncate">{{ item.name }}</span>
                    </router-link>
                </nav>

                <!-- Footer Sidebar -->
                <div class="p-4 border-t border-border">
                    <router-link to="/" class="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors">
                        <ToolboxIcon name="Logout" :size="20" :class="isSidebarOpen ? 'mr-3' : 'mx-auto'" />
                        <span v-if="isSidebarOpen">Quitter</span>
                    </router-link>
                </div>
            </div>
        </aside>

        <!-- Main Content -->
        <main class="flex-1 flex flex-col min-w-0 overflow-hidden">
            <!-- Impersonation Banner -->
            <div v-if="impersonatedUser" class="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between text-sm font-bold shadow-lg z-50">
                <div class="flex items-center gap-2">
                    <ToolboxIcon name="User" :size="18" />
                    <span>Mode Impersonnalisation : vous agissez en tant que <span class="underline">{{ userData?.email }}</span></span>
                </div>
                <button 
                    @click="stopImpersonation"
                    class="bg-amber-950 text-amber-500 px-3 py-1 rounded-md hover:bg-amber-900 hover:text-amber-400 transition-colors flex items-center gap-1"
                >
                    <ToolboxIcon name="Logout" :size="14" />
                    Arrêter
                </button>
            </div>

            <!-- Header/TopBar if needed -->
            <header class="h-16 border-b border-border flex items-center px-8 bg-card/50 backdrop-blur-sm">
                <h1 class="text-lg font-semibold">{{ route.meta.title || 'Toolbox' }}</h1>
            </header>

            <!-- Scrollable content area -->
            <div class="flex-1 overflow-y-auto p-8">
                <router-view v-slot="{ Component }">
                    <transition name="fade" mode="out-in">
                        <component :is="Component" />
                    </transition>
                </router-view>
            </div>
        </main>
    </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Custom scrollbar for sidebar */
nav::-webkit-scrollbar {
    width: 4px;
}
nav::-webkit-scrollbar-track {
    background: transparent;
}
nav::-webkit-scrollbar-thumb {
    @apply bg-border rounded-full;
}
</style>
